import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {KennethMustDie} from "../KennethMustDie";
import {gsap} from "gsap";
import {ISimplePresentationRunnerController} from "../../core/winpresentation/SimplePresentationRunner";
import {IBigWinLevel} from "../server/IKMDData";
import {KMDGameMode} from "../server/KMDGameMode";
import {KMDSound} from "../sound/KMDSound";

export class KMDBigWin extends PIXI.Container implements StageLayer {

    private _item: PIXI.spine.Spine;
    private _level6: PIXI.spine.Spine;
    private _soundName: string = "";

    constructor() {
        super();
        this.name = "KMDBigWin";

        this.init();
    }

    public playBigWin(flowStateController: ISimplePresentationRunnerController): void {
        const levels: IBigWinLevel[] = KennethMustDie.model.getBigWinLevels(),
            tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.1,
                onComplete: () => {
                    flowStateController.finish();
                }
            }),
            useTotalWin: boolean = (KennethMustDie.model.gameData.mode !== KMDGameMode.NORMAL || KennethMustDie.model.gameData.nextMode !== KMDGameMode.NORMAL),
            winAmount: number = KennethMustDie.model.gameData.totalAvalancheRoundWinnings;

        this.precalculateScale(winAmount);

        tl.add(() => {
            KennethMustDie.countUp.getReady(600);

            KennethMustDie.blackBg.show(0.8);
        });

        for (let level of levels) {
            tl.add(() => {
                flowStateController.setSkipCallback(() => {
                    // #228
                    if (this._soundName) {
                        KennethMustDie.soundPlayer.stopSound(this._soundName);
                    }

                    tl.seek(level.name + "_end", false);

                    if (levels.length === level.id) {
                        gsap.to(KennethMustDie.countUp.scale, {duration: 1.5, x: 1.1, y: 1.1, ease: "bounce"});
                    }
                });

                // #228, #506
                if ((level.id === 1) || (KennethMustDie.model.gameData.wasFeatureBuy && level.id === 2)) {
                    KennethMustDie.soundPlayer.fadeMusic({
                        name: "",
                        duration: 200,
                        to: 0
                    });
                }
            });

            tl.add(this.playLevelAnimation(level));
            tl.addLabel(level.name + "_end");
            tl.add(() => flowStateController.setSkipCallback());
        }
        tl.add(() => {
            KennethMustDie.countUp.stopAt(winAmount);
            KennethMustDie.setKeypadWin(useTotalWin);

            // #228, #500
            if (!KennethMustDie.model.gameData.brokeTheBank) {
                KennethMustDie.soundPlayer.fadeMusic({
                    name: "",
                    duration: 1200,
                    to: 1
                });
            }
            KennethMustDie.soundPlayer.playSound(KMDSound.BIG_WIN_END);
        });
        tl.to(this, {
            duration: 1
        });
        tl.add(this.end());
    }

    private init(): void {
        this._item = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("bigWin"));
        this._item.visible = false;
        this._item.position.set(360);

        this._level6 = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("bigWinLevel6"));
        this._level6.visible = false;
        this._level6.position.set(360);

        this.addChild(this._item, this._level6);
    }

    private playLevelAnimation(level: IBigWinLevel): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            scale: gsap.core.Timeline = gsap.timeline(),
            playedBet: number = KennethMustDie.model.gameData.playedBetValue,
            countFrom: number = level.from * playedBet,
            countTo: number = level.to * playedBet,
            id: number = level.id,
            duration: number = level.duration;
        let item: PIXI.spine.Spine = this._item,
            animName: string = "Lvl_0" + id;

        if (id === 6) {
            item = this._level6;
            animName = "animation";

            this.getLevel6Animation(tl, countFrom);
        } else {
            // #477
            if (id === 2 && KennethMustDie.useCensor) {
                animName += "_blur";
            }

            scale.add(this.getCountUpScaleAnimation(0.1, "power2.in", 1.2));
            scale.add(this.getCountUpScaleAnimation(0.2, "power2.out", 1));
            scale.add(this.getCountUpScaleAnimation(tl.duration() - 0.3, "power2.in", 0.8));

            tl.add(scale, 0);
        }

        if (!item.visible) {
            tl.add(() => {
                item.visible = true;
                item.updateTransform();
            }, 0);
        }
        tl.add([
            () => {
                item.skeleton.setSlotsToSetupPose();
                item.skeleton.setToSetupPose();
                item.state.setEmptyAnimation(0, 0);

                // #228
                KennethMustDie.soundPlayer.playSound(KMDSound.BIG_WIN_LEVEL_CHANGE);
                this._soundName = KMDSound.BIG_WIN_LEVEL[id - 1];
                KennethMustDie.soundPlayer.playSound(this._soundName);
            },
            KennethMustDie.countUp.getCountAnimation(countFrom, countTo, duration),
            KennethMustDie.createTimelineForAnimation(item, 0, animName, false).repeat((id === 6 ? 2 : 0))
        ], 0);

        // #494
        if (id === 6) {
            tl.to(this, {
                duration: 1
            });
            tl.add(KennethMustDie.createTimelineForAnimation(item, 0, animName, false));
        }

        return tl;
    }

    private precalculateScale(endValue: number): void {
        KennethMustDie.countUp.stopAt(0);
        KennethMustDie.countUp.preCalculateScale(0, endValue);
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(KennethMustDie.countUp.scale, {
            duration: duration,
            x: to,
            y: to,
            ease: ease
        });

        return tl;
    }

    private getLevel6Animation(tl: gsap.core.Timeline, countFrom: number): void {
        const finalAmount: number = KennethMustDie.model.gameData.totalAvalancheRoundWinnings,
            pumpDuration: number = (4 / 30),
            pumpCount: number = 6,
            perPumpValueAddition: number = (finalAmount - countFrom) / pumpCount;
        let toScale: number = 1.15,
            amount: number;

        tl.add(() => {
            this._item.visible = false;
        }, 0);
        tl.to(KennethMustDie.countUp, {
            duration: 2 * pumpDuration,
            y: 440
        }, 0);

        for (let ctr: number = 0; ctr < pumpCount; ctr++) {
            if (ctr === (pumpCount - 1)) {
                toScale = 1.3;
            }

            tl.add(() => {
                amount = Math.floor(perPumpValueAddition + KennethMustDie.countUp.prevValue);
                if (ctr === (pumpCount - 1)) {
                    amount = finalAmount;
                }
                KennethMustDie.countUp.stopAt(amount);
            });
            tl.to(KennethMustDie.countUp.scale, {
                duration: pumpDuration,
                x: toScale,
                y: toScale
            });

            tl.to(KennethMustDie.countUp.scale, {
                duration: pumpDuration,
                x: 1,
                y: 1
            });

            tl.to(this, {
                duration: ((ctr % 2 === 0) ? (11 / 30) : (2 * pumpDuration))
            });
        }

        tl.add(() => {
            KennethMustDie.countUp.stopAt(finalAmount);
        });
    }

    private end(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.5;

        if (!KennethMustDie.model.gameData.brokeTheBank) {
            tl.add(KennethMustDie.blackBg.hide(duration), 0);
        }

        tl.to([this._item, KennethMustDie.countUp, this._level6], {
            duration: duration,
            alpha: 0
        }, 0);

        tl.add(() => {
            this._item.visible = false;
            this._item.alpha = 1;
            this._level6.visible = false;
            this._level6.alpha = 1;

            KennethMustDie.countUp.visible = false;

            this._soundName = "";
        });

        return tl;
    }
}